// State machine for the tutorial player.
//
// Models the player as an explicit reducer over { currentCardId, branchPath,
// inputValues, completedAt, terminalOutcome }. The reference behavior is the
// iOS TutorialPlayerViewModel; if branching ever grows past simple
// first-match-wins this should port cleanly to XState.
export function initialState(startCardId) {
    return {
        currentCardId: startCardId,
        branchPath: [startCardId],
        inputValues: {},
        completedAt: null,
        terminalOutcome: null,
        outcomeCode: null,
    };
}
export function reducer(state, action) {
    switch (action.type) {
        case 'SUBMIT_INPUT': {
            return {
                ...state,
                inputValues: { ...state.inputValues, [action.cardId]: action.value },
            };
        }
        case 'ADVANCE': {
            const card = action.cards.find((c) => c.id === state.currentCardId);
            if (!card)
                return state;
            // Terminal cards finish the run.
            if (card.is_terminal) {
                if (state.completedAt)
                    return state;
                // Prefer outcome_code, fall back to legacy terminal_outcome.
                const outcomeCode = card.outcome_code ?? card.terminal_outcome ?? null;
                return {
                    ...state,
                    completedAt: new Date().toISOString(),
                    terminalOutcome: card.terminal_outcome,
                    outcomeCode,
                };
            }
            const nextId = resolveNext(card, state.inputValues[card.id]);
            if (!nextId) {
                // Walked off the end without a terminal card — treat as success.
                return {
                    ...state,
                    completedAt: new Date().toISOString(),
                    terminalOutcome: 'success',
                    outcomeCode: 'success',
                };
            }
            return {
                ...state,
                currentCardId: nextId,
                branchPath: [...state.branchPath, nextId],
            };
        }
        case 'RESET':
            return initialState(action.startCardId);
    }
}
function resolveNext(card, latestInput) {
    for (const branch of card.branches) {
        if (matches(branch, latestInput))
            return branch.target_card_id;
    }
    return card.next_card_id;
}
function matches(branch, value) {
    switch (branch.operator) {
        case 'is_truthy':
            return !!value && value !== 'false' && value !== '0';
        case 'is_falsy':
            return !value || value === 'false' || value === '0';
        case 'equals':
            return value !== undefined && asString(branch.value) === value;
        case 'not_equals':
            return value !== undefined && asString(branch.value) !== value;
        case 'in': {
            const list = Array.isArray(branch.value) ? branch.value : [];
            return value !== undefined && list.includes(value);
        }
    }
}
function asString(v) {
    if (Array.isArray(v))
        return v[0] ?? '';
    return v ?? '';
}
/** Pick the start card. Falls back to the lowest-sort_order card if no flag set. */
export function findStartCard(cards) {
    return (cards.find((c) => c.is_start) ??
        [...cards].sort((a, b) => a.sort_order - b.sort_order)[0]);
}
/** Find the input block on a card, if any. v2 cards have at most one. */
export function findInputBlock(card) {
    return card.blocks.find((b) => b.type === 'input');
}
